'use client';

import { useQueryState } from 'nuqs';
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormManifestContext } from './FormBuilder';
import { MEETING_ASIDE_KEY, SUCCESS_STEP_KEY } from './stepKeys';
import { createStepKeyParser } from './utils/stepParser';
import { useFormContext } from 'react-hook-form';
const TEST_SUCCESS_STEP = false;
type UseStepNavigationArgs = Partial<{
  initialStep: string;
}>;
export function useStepNavigation({
  initialStep
}: UseStepNavigationArgs = {}) {
  const manifest = use(FormManifestContext);
  const form = useFormContext();
  const allStepKeys = useMemo(() => {
    const sectionSteps = manifest.steps.map(s => s._key);
    return [...sectionSteps, MEETING_ASIDE_KEY, SUCCESS_STEP_KEY];
  }, [manifest.steps]);
  const meetingAsideIntentRef = useRef(false);
  const defaultStepKey = initialStep || manifest.steps[0]?._key || '';
  const parser = useMemo(() => createStepKeyParser(manifest.steps).withDefault(defaultStepKey).withOptions({
    history: 'push',
    clearOnDefault: false
  }), [manifest.steps, defaultStepKey]);
  const [activeStep, setActiveStep] = useQueryState('s', parser);
  const initialVisitedStep = activeStep || defaultStepKey;
  const [visitedStepKeys, setVisitedStepKeys] = useState<Set<string>>(() => new Set(initialVisitedStep ? [initialVisitedStep] : []));
  const [submittedStepKeys, setSubmittedStepKeys] = useState<Set<string>>(() => new Set());
  const visitStep = useCallback((step: string) => {
    setVisitedStepKeys(prev => {
      if (prev.has(step)) return prev;
      const next = new Set(prev);
      next.add(step);
      return next;
    });
  }, []);
  const submitStep = useCallback((step: string) => {
    setSubmittedStepKeys(prev => {
      if (prev.has(step)) return prev;
      const next = new Set(prev);
      next.add(step);
      return next;
    });
  }, []);
  const visibleStepsCount = allStepKeys.length;
  const activeStepIndex = useMemo(() => {
    const i = allStepKeys.indexOf(activeStep);
    return i === -1 ? 0 : i;
  }, [activeStep, allStepKeys]);
  const visibleSteps = allStepKeys;
  const displayStepNumber = activeStepIndex + 1;
  const isSuccessStep = activeStep === SUCCESS_STEP_KEY;
  const isMeetingAside = activeStep === MEETING_ASIDE_KEY;
  const canGoForward = useMemo(() => {
    if (isSuccessStep || isMeetingAside) return false;
    // The submit step is the last manifest step (index = manifest.steps.length - 1).
    // Success and meeting aside are never reached via Next.
    return activeStepIndex < manifest.steps.length - 1;
  }, [activeStepIndex, manifest.steps.length, isSuccessStep, isMeetingAside]);
  const canGoBackward = useMemo(() => {
    if (isSuccessStep || isMeetingAside) return false;
    return activeStepIndex > 0;
  }, [activeStepIndex, isSuccessStep, isMeetingAside]);
  const canVisitStep = useCallback((step: string) => {
    // Success and meeting aside are never navigable via the sidebar.
    if (step === SUCCESS_STEP_KEY || step === MEETING_ASIDE_KEY) return false;
    const targetIndex = allStepKeys.indexOf(step);
    return targetIndex !== -1 && (targetIndex <= activeStepIndex || visitedStepKeys.has(step));
  }, [allStepKeys, activeStepIndex, visitedStepKeys]);
  const getFieldIdsAfterStep = useCallback((targetStep: string) => {
    const targetIndex = manifest.steps.findIndex(s => s._key === targetStep);
    if (targetIndex < 0) return [];
    return manifest.steps.slice(targetIndex + 1).flatMap(step => manifest.fieldKeysBySection.get(step._key) ?? []);
  }, [manifest.steps, manifest.fieldKeysBySection]);
  const getFieldIdsFromStep = useCallback((targetStep: string) => {
    const targetIndex = manifest.steps.findIndex(s => s._key === targetStep);
    if (targetIndex < 0) return [];
    return manifest.steps.slice(targetIndex).flatMap(step => manifest.fieldKeysBySection.get(step._key) ?? []);
  }, [manifest.steps, manifest.fieldKeysBySection]);
  const clearNewFieldErrors = useCallback((fieldIds: string[], existingErrorFields: Set<string>) => {
    const newErrorFields = fieldIds.filter(fieldId => !existingErrorFields.has(fieldId) && form.getFieldState(fieldId).error);
    if (newErrorFields.length) {
      form.clearErrors(newErrorFields);
    }
  }, [form]);
  const validateCurrentStep = useCallback(async () => {
    const currentStep = activeStep;
    submitStep(currentStep);
    const fieldIds = manifest.fieldKeysBySection.get(currentStep) ?? manifest.fieldsBySection.get(currentStep)?.map(f => f.id) ?? [];
    const futureFieldIds = getFieldIdsAfterStep(currentStep);
    const existingFutureErrorFields = new Set(futureFieldIds.filter(fieldId => form.getFieldState(fieldId).error));
    const ok = fieldIds.length === 0 || (await form.trigger(fieldIds));
    clearNewFieldErrors(futureFieldIds, existingFutureErrorFields);
    return ok;
  }, [activeStep, clearNewFieldErrors, form, getFieldIdsAfterStep, manifest.fieldKeysBySection, manifest.fieldsBySection, submitStep]);
  const getFieldIdsBeforeStep = useCallback((targetStep: string) => {
    const targetIndex = manifest.steps.findIndex(s => s._key === targetStep);
    if (targetIndex < 0) return [];
    return manifest.steps.slice(0, targetIndex).flatMap(step => manifest.fieldKeysBySection.get(step._key) ?? []);
  }, [manifest.steps, manifest.fieldKeysBySection]);
  const nextStepName = useMemo(() => {
    const nextId = allStepKeys[activeStepIndex + 1];
    if (!nextId || nextId === SUCCESS_STEP_KEY || nextId === MEETING_ASIDE_KEY) {
      return '';
    }
    const nextStep = manifest.steps.find(s => s._key === nextId);
    return nextStep?.name ?? '';
  }, [allStepKeys, activeStepIndex, manifest.steps]);
  const nextStep = useCallback(async (): Promise<boolean> => {
    if (!canGoForward) return false;
    const ok = await validateCurrentStep();
    if (!ok) return false;
    const nextStepKey = allStepKeys[activeStepIndex + 1];
    visitStep(nextStepKey);
    await setActiveStep(nextStepKey);
    return true;
  }, [canGoForward, validateCurrentStep, visitStep, setActiveStep, allStepKeys, activeStepIndex]);
  const previousStep = useCallback(() => {
    if (!canGoBackward) return;
    const previousStepKey = allStepKeys[activeStepIndex - 1];
    visitStep(previousStepKey);
    void setActiveStep(previousStepKey);
  }, [canGoBackward, allStepKeys, activeStepIndex, visitStep, setActiveStep]);
  const syncActiveStepToUrl = useCallback(() => {
    if (!activeStep) return;
    void setActiveStep(activeStep);
  }, [activeStep, setActiveStep]);
  const clearStepSearchParam = useCallback(() => {
    void setActiveStep(null);
  }, [setActiveStep]);
  const goToStep = useCallback((step: string) => {
    const isProgrammaticPseudo = step === SUCCESS_STEP_KEY || step === MEETING_ASIDE_KEY;
    if (!isProgrammaticPseudo && !canVisitStep(step)) return;
    if (step === MEETING_ASIDE_KEY) {
      meetingAsideIntentRef.current = true;
    }
    const fieldIds = getFieldIdsBeforeStep(step);
    if (fieldIds.length) {
      const futureFieldIds = getFieldIdsFromStep(step);
      const existingFutureErrorFields = new Set(futureFieldIds.filter(fieldId => form.getFieldState(fieldId).error));
      void form.trigger(fieldIds, {
        shouldFocus: false
      }).then(() => clearNewFieldErrors(futureFieldIds, existingFutureErrorFields));
    }
    visitStep(step);
    void setActiveStep(step);
  }, [canVisitStep, clearNewFieldErrors, form, getFieldIdsBeforeStep, getFieldIdsFromStep, visitStep, setActiveStep]);
  useEffect(() => {
    if (activeStep !== MEETING_ASIDE_KEY) {
      meetingAsideIntentRef.current = false;
    }
  }, [activeStep]);
  useEffect(() => {
    if (!activeStep || !allStepKeys.length) return;
    if (!allStepKeys.includes(activeStep)) {
      void setActiveStep(allStepKeys[0]);
    }
  }, [activeStep, allStepKeys, setActiveStep]);
  const progressSteps = useMemo(() => {
    return allStepKeys.filter(s => s !== SUCCESS_STEP_KEY && s !== MEETING_ASIDE_KEY);
  }, [allStepKeys]);
  const progressStepsCount = progressSteps.length;
  const progressStepIndex = useMemo(() => {
    return progressSteps.indexOf(activeStep);
  }, [activeStep, progressSteps]);
  const progressPercentage = useMemo(() => {
    if (isSuccessStep || isMeetingAside || TEST_SUCCESS_STEP) return 100;
    const current = progressSteps.indexOf(activeStep);
    if (current === -1) return 0;
    return (current ? current + 1 : 1) / (progressStepsCount ? progressStepsCount + 2 : 1) * 100;
  }, [activeStep, progressStepsCount, progressSteps, isSuccessStep, isMeetingAside]);
  const progressTextValue = useMemo(() => {
    if (isSuccessStep) return 'Submitted';
    if (isMeetingAside) return 'Book a meeting';
    if (!activeStep) return `${progressStepsCount} ${progressStepsCount === 1 ? 'step' : 'steps'}`;
    return `Step ${progressStepIndex + 1} of ${progressStepsCount}`;
  }, [activeStep, progressStepIndex, progressStepsCount, isSuccessStep, isMeetingAside]);
  const activeStepLabel = useMemo(() => {
    if (isSuccessStep || isMeetingAside) return '';
    const step = manifest.steps.find(s => s._key === activeStep);
    return step?.name ?? activeStep;
  }, [manifest.steps, activeStep, isSuccessStep, isMeetingAside]);
  return {
    activeStep,
    visibleSteps,
    visitedStepKeys,
    submittedStepKeys,
    activeStepIndex,
    visibleStepsCount,
    activeStepLabel,
    canGoForward,
    canGoBackward,
    canVisitStep,
    validateCurrentStep,
    syncActiveStepToUrl,
    clearStepSearchParam,
    nextStep,
    previousStep,
    goToStep,
    displayStepNumber,
    progressPercentage,
    progressTextValue,
    isSuccessStep,
    isMeetingAside,
    meetingAsideIntentRef,
    nextStepName
  };
}